import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { websocketService } from '../services/websocketService';
import { showToast } from '../components/Toast';
import StatusBadge from '../components/StatusBadge';
import BatteryMeter from '../components/BatteryMeter';
import CompetitionBottomNav from '../components/CompetitionBottomNav';
import ConfirmModal from '../components/ConfirmModal';
import { getTeamMeta, formatScoreAbbreviated, formatPercent, projectBatteryLoss, MAX_WEEKLY_BATTERY_LOSS_PCT, TeamIcon } from '../utils/transformersCompetition';
import RoundCountdown from '../components/RoundCountdown';
import transformersHero from '../assets/transformers-hero.webp';
import './transformersCompetitionShared.css';

function InfoModal({ title, onClose, children }) {
  return (
    <div className="tc-sheet-overlay" onClick={onClose}>
      <div className="tc-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="tc-sheet-header">
          <h2>{title}</h2>
          <button type="button" className="tc-sheet-close" onClick={onClose}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function BatteryInfoModal({ onClose }) {
  return (
    <InfoModal title="Energon Battery" onClose={onClose}>
      <p>
        Your Energon Battery is your survival meter for the Grand Champion race. Everyone starts
        the season at 100%.
      </p>
      <p>
        <strong>You lose Battery</strong> when you miss your weekly Target, scaled to how far you
        missed - miss by half and you lose half the maximum, so a 50% miss costs about 17%. The most
        a single week can take is 34%, and only a total miss reaches it. Not playing at all counts
        as a 100% miss, so it always costs the full 34%.
      </p>
      <p>
        <strong>You gain Battery</strong> only one way: your team winning that week's Team Energon
        Ranking. If your team's combined Surplus is the highest of the three teams, every teammate
        still in contention gets a recharge - the bonus starts strong and shrinks to nothing by
        the second half of the season.
      </p>
      <p>
        <strong>Hitting 0% is Stasis Lock</strong> - you're permanently out of the running for
        Grand Champion (your Battery and Rust freeze right there). You can still play every week,
        still earn Surplus, and still help your team win - you just can't win the title yourself
        anymore.
      </p>
    </InfoModal>
  );
}

function RustInfoModal({ onClose }) {
  return (
    <InfoModal title="Rust Points" onClose={onClose}>
      <p>
        Rust tracks how long it's been since you last played. It exists so a long stretch of
        skipped weeks makes it harder to jump back in and immediately compete, not just costs
        Battery.
      </p>
      <p>
        <strong>Skip a week:</strong> +1 Rust point. <strong>Play a week</strong> (win or lose):
        -1 Rust point, down to a floor of 0.
      </p>
      <p>
        Each Rust point adds 5% to your Target for the week - 3 Rust points means your target is
        15% higher than it would otherwise be. Playing regularly is the only way to work it back
        down.
      </p>
    </InfoModal>
  );
}

function SurplusInfoModal({ week, surplusPercent, onClose }) {
  return (
    <InfoModal title="Energon Surplus" onClose={onClose}>
      <p>
        Surplus measures how far above your Target your best score landed, as a percentage - the
        bigger the beat, the bigger the Surplus.
      </p>
      <p>
        Surplus never recharges your own Battery directly. Instead, it adds to your team's weekly
        Energon Total - the three teams are ranked by their combined Surplus each week, and
        whichever team comes out on top gets a Battery recharge for every member still in
        contention.
      </p>
      <p>
        Even if you're already Stasis Locked and out of the Grand Champion race, your Surplus
        still counts fully toward your team's total - beating your target always helps your
        teammates.
      </p>
      {/* Derived from the score rather than week.energonSurplus, which the server only writes when
          the round closes and would otherwise read as "no Surplus" all week long. */}
      {surplusPercent > 0 ? (
        <p style={{ marginTop: 10, fontWeight: 600 }}>
          This week: +{formatPercent(surplusPercent, 1)} Surplus for your team.
        </p>
      ) : (
        <p style={{ marginTop: 10, fontWeight: 600 }}>
          This week: no Surplus, since you're below target. What that costs you in Battery is on
          the Battery tile, separately.
        </p>
      )}
    </InfoModal>
  );
}

// The week's Battery outcome, laid out as the same score/target/result breakdown the Target modal
// uses. For a completed week these are the numbers the server actually charged; for the
// in-progress week they're projected off the current best score, so a player can see mid-week what
// the week is costing them and what closing the gap would save.
function BatteryImpactModal({
  week, isCompleted, frozen, hasScore, batteryLoss, missPercent, surplusPercent, currentBattery, onClose
}) {
  const beforeValue = isCompleted ? week.batteryBefore : currentBattery;
  const afterValue = isCompleted
    ? week.batteryAfter
    : (beforeValue === null || beforeValue === undefined
        ? null
        : Math.min(100, Math.max(0, beforeValue - (batteryLoss || 0))));
  const showBatteryRow = beforeValue !== null && beforeValue !== undefined
    && afterValue !== null && afterValue !== undefined;
  const lossColor = batteryLoss > 0 ? 'var(--tc-batt-crit)' : 'var(--tc-batt-good)';

  return (
    <InfoModal title={`Week ${week.roundNumber} Battery`} onClose={onClose}>
      <div className="tc-calc-row">
        <span className="tc-calc-label">Your best score</span>
        <span className="tc-calc-value">{hasScore ? formatScoreAbbreviated(week.score) : 'None yet'}</span>
      </div>
      <div className="tc-calc-row">
        <span className="tc-calc-label">Your target</span>
        <span className="tc-calc-value">{formatScoreAbbreviated(week.targetWithRust)}</span>
      </div>
      <div className="tc-calc-row tc-calc-sub">
        <span className="tc-calc-label">
          {!hasScore ? 'Counts as' : (missPercent !== null ? 'Short by' : 'Above target by')}
        </span>
        <span className="tc-calc-value">
          {!hasScore ? 'A full miss' : formatPercent(missPercent !== null ? missPercent : surplusPercent, 1)}
        </span>
      </div>

      {frozen ? (
        <div className="tc-calc-row tc-calc-final">
          <span className="tc-calc-label">{isCompleted ? 'Battery change' : 'Battery at risk'}</span>
          <span className="tc-calc-value">None, you're frozen</span>
        </div>
      ) : (
        <>
          <div className={showBatteryRow ? 'tc-calc-row' : 'tc-calc-row tc-calc-final'}>
            <span className="tc-calc-label">{isCompleted ? 'Battery lost' : 'Battery at risk'}</span>
            <span className="tc-calc-value" style={{ color: lossColor }}>
              {batteryLoss > 0 ? `-${formatPercent(batteryLoss, 1)}` : 'Nothing'}
            </span>
          </div>
          {week.batteryRecovery > 0 && (
            <div className="tc-calc-row">
              <span className="tc-calc-label">Team win recharge</span>
              <span className="tc-calc-value" style={{ color: 'var(--tc-batt-good)' }}>
                +{formatPercent(week.batteryRecovery, 1)}
              </span>
            </div>
          )}
          {showBatteryRow && (
            <div className="tc-calc-row tc-calc-final">
              <span className="tc-calc-label">
                {isCompleted ? 'Your Battery' : 'Your Battery if the week closes here'}
              </span>
              <span className="tc-calc-value">
                {formatPercent(beforeValue)} &rarr; {formatPercent(afterValue)}
              </span>
            </div>
          )}
        </>
      )}

      {frozen ? (
        <p className="tc-form-hint" style={{ marginTop: 12 }}>
          You're in Stasis Lock, so your Battery and Rust stay where they are no matter how this
          week goes. Anything you score above target still counts as Surplus for your team.
        </p>
      ) : (
        <p className="tc-form-hint" style={{ marginTop: 12 }}>
          {!hasScore && !isCompleted
            ? `A week with no score counts as a full miss, the most a single week can take (${MAX_WEEKLY_BATTERY_LOSS_PCT}%). Posting any score at all cuts that down. `
            : ''}
          Battery loss scales with the size of your miss: the miss as a percent of your target,
          times the {MAX_WEEKLY_BATTERY_LOSS_PCT}% weekly maximum. Miss by half and you lose about
          half the maximum. Reach your target and you lose nothing at all
          {isCompleted ? '.' : ', so every point you add this week shrinks the hit.'}
        </p>
      )}
    </InfoModal>
  );
}

function ScoreInfoModal({ onClose }) {
  return (
    <InfoModal title="Your Best Score" onClose={onClose}>
      <p>
        You can play the machine as many times as you like during the week - only your single
        best game counts. Any lower scores from other attempts are simply ignored.
      </p>
      <p>
        Your best score is what gets compared against your Target to work out the week's result:
        beat it and you generate Surplus for your team with no Battery loss; miss it (or don't
        play at all) and you take Battery damage.
      </p>
    </InfoModal>
  );
}

// Rows shared by every formula version: the WeekPercent step, the Rust step and the final total.
function TargetCalculationTail({ week }) {
  return (
    <>
      <div className="tc-calc-row tc-calc-sub">
        <span className="tc-calc-label">× Week Percent ({formatPercent((week.weekPercent || 1) * 100, 0)})</span>
        <span className="tc-calc-value">{formatScoreAbbreviated(week.targetScore)}</span>
      </div>
      <div className="tc-calc-row tc-calc-sub">
        <span className="tc-calc-label">+ Rust ({week.rustPointsApplied} pt{week.rustPointsApplied === 1 ? '' : 's'} × 5%)</span>
        <span className="tc-calc-value">{formatScoreAbbreviated(week.targetWithRust)}</span>
      </div>
      <div className="tc-calc-row tc-calc-final">
        <span className="tc-calc-label">Your Target</span>
        <span className="tc-calc-value">{formatScoreAbbreviated(week.targetWithRust)}</span>
      </div>
    </>
  );
}

/**
 * The original formula, used for the 2026 season's weeks up to the mid-season cutover: a flat
 * 50/50 blend of the machine average and your own average. Kept so those weeks keep explaining
 * themselves with the rule that actually produced their numbers. Delete this whole component (and
 * the branch that selects it in TargetCalculationModal) once no week anywhere still reports
 * targetFormulaVersion 1.
 */
function TargetCalculationV1({ week }) {
  const isWeekOne = week.roundNumber === 1;
  const blended = !isWeekOne && week.machineAverageUsed !== null && week.personalAverageUsed !== null
    ? (week.machineAverageUsed + week.personalAverageUsed) / 2
    : null;

  if (isWeekOne) {
    return (
      <>
        <div className="tc-calc-row">
          <span className="tc-calc-label">Machine Average</span>
          <span className="tc-calc-value">{formatScoreAbbreviated(week.targetScore)}</span>
        </div>
        <p className="tc-form-hint">
          Week 1 target is simply the machine's average score - everyone starts on the same baseline.
        </p>
        <div className="tc-calc-row tc-calc-final">
          <span className="tc-calc-label">Your Target</span>
          <span className="tc-calc-value">{formatScoreAbbreviated(week.targetWithRust)}</span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="tc-calc-row">
        <span className="tc-calc-label">Machine Average</span>
        <span className="tc-calc-value">{week.machineAverageUsed !== null ? formatScoreAbbreviated(week.machineAverageUsed) : ' - '}</span>
      </div>
      <div className="tc-calc-row">
        <span className="tc-calc-label">Your Average Score</span>
        <span className="tc-calc-value">{week.personalAverageUsed !== null ? formatScoreAbbreviated(week.personalAverageUsed) : ' - '}</span>
      </div>
      <div className="tc-calc-row tc-calc-sub">
        <span className="tc-calc-label">Blended Average / 2</span>
        <span className="tc-calc-value">{blended !== null ? formatScoreAbbreviated(blended) : ' - '}</span>
      </div>
      <TargetCalculationTail week={week} />
    </>
  );
}

/**
 * The current formula. The machine average is only a starting estimate: it counts for one week's
 * worth of evidence and its pull shrinks with every week you play. Your own average is then a
 * floor, so your target is never below the level you have already shown.
 */
function TargetCalculationV2({ week }) {
  const weeksPlayed = week.weeksPlayedUsed || 0;
  const machineAverage = week.machineAverageUsed;
  const ownAverage = week.personalAverageUsed !== null ? week.personalAverageUsed : week.machineAverageUsed;

  // No weeks played yet, so there is nothing but the machine average to go on and both steps of
  // the formula collapse onto it. Showing a blend and a floor of the same number would be noise.
  if (weeksPlayed === 0 || machineAverage === null || ownAverage === null) {
    return (
      <>
        <div className="tc-calc-row">
          <span className="tc-calc-label">Machine Average</span>
          <span className="tc-calc-value">{machineAverage !== null ? formatScoreAbbreviated(machineAverage) : ' - '}</span>
        </div>
        <p className="tc-form-hint">
          You had no scores on the board yet this season, so your target starts from the machine's
          average score. Once you have played a week, your own average takes over.
        </p>
        <TargetCalculationTail week={week} />
      </>
    );
  }

  const blend = (machineAverage + weeksPlayed * ownAverage) / (1 + weeksPlayed);
  const floorWins = ownAverage >= blend;

  return (
    <>
      <div className="tc-calc-row">
        <span className="tc-calc-label">Machine Average</span>
        <span className="tc-calc-value">{formatScoreAbbreviated(machineAverage)}</span>
      </div>
      <div className="tc-calc-row">
        <span className="tc-calc-label">Your Average Score ({weeksPlayed} week{weeksPlayed === 1 ? '' : 's'})</span>
        <span className="tc-calc-value">{formatScoreAbbreviated(ownAverage)}</span>
      </div>
      <div className="tc-calc-row tc-calc-sub">
        <span className="tc-calc-label">Weighted Blend (machine average counts for 1 of {1 + weeksPlayed} weeks)</span>
        <span className="tc-calc-value">{formatScoreAbbreviated(blend)}</span>
      </div>
      <div className="tc-calc-row tc-calc-sub">
        <span className="tc-calc-label">Higher of the two ({floorWins ? 'your average' : 'the blend'})</span>
        <span className="tc-calc-value">{formatScoreAbbreviated(Math.max(blend, ownAverage))}</span>
      </div>
      <TargetCalculationTail week={week} />
      <p className="tc-form-hint">
        {floorWins
          ? 'Your own average is higher than the blend, so that is what your target is built from - you are never asked for less than you have already shown you can do.'
          : 'The machine average is still pulling your target up a little. Its share shrinks every week you play.'}
      </p>
    </>
  );
}

function TargetCalculationModal({ week, onClose }) {
  const version = week.targetFormulaVersion || 1;

  return (
    <div className="tc-sheet-overlay" onClick={onClose}>
      <div className="tc-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="tc-sheet-header">
          <h2>Week {week.roundNumber} Target</h2>
          <button type="button" className="tc-sheet-close" onClick={onClose}>&times;</button>
        </div>

        {version >= 2 ? <TargetCalculationV2 week={week} /> : <TargetCalculationV1 week={week} />}

        {version < 2 && (
          <p className="tc-form-hint">
            This week used the original target formula, a flat 50/50 blend of the machine average
            and your own average. Later weeks use the current formula, where the machine average is
            only a starting estimate that fades as you play, and your own average is a floor.
          </p>
        )}
      </div>
    </div>
  );
}

function WeekCard({ week, defaultExpanded, currentBattery, currentlyStasisLocked }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showSurplusModal, setShowSurplusModal] = useState(false);
  const [showBatteryImpactModal, setShowBatteryImpactModal] = useState(false);
  const isCompleted = week.roundStatus === 'completed';
  // did_play is only written when the round closes, so the in-progress week has to read the score
  // itself to know whether anything has been posted yet.
  const hasScore = week.score > 0;
  const missPercent = hasScore && week.score < week.targetWithRust
    ? ((week.targetWithRust - week.score) / week.targetWithRust) * 100
    : null;
  // energonSurplus is likewise only stored at close, so the beat is derived the same way.
  const surplusPercent = hasScore && missPercent === null && week.targetWithRust > 0
    ? ((week.score - week.targetWithRust) / week.targetWithRust) * 100
    : null;
  const deltaPoints = hasScore ? week.score - week.targetWithRust : null;
  const isAhead = deltaPoints !== null && deltaPoints >= 0;
  const deltaColor = deltaPoints === null ? 'var(--tc-text-dim)' : (isAhead ? 'var(--tc-batt-good)' : 'var(--tc-batt-crit)');

  // was_stasis_locked_as_of_week is only written when a round closes, so the in-progress week
  // has to fall back to the player's live lock state.
  const frozen = isCompleted ? !!week.wasStasisLocked : !!currentlyStasisLocked;
  // Completed weeks report what the server actually charged; the in-progress week is projected
  // from the current best score with the same formula.
  const batteryLoss = frozen
    ? null
    : (isCompleted ? week.batteryLoss : projectBatteryLoss(week.targetWithRust, week.score));
  const batteryLossColor = batteryLoss === null
    ? 'var(--tc-text-dim)'
    : (batteryLoss > 0 ? 'var(--tc-batt-crit)' : 'var(--tc-batt-good)');

  return (
    <div className="tc-section">
      <button type="button" className="tc-week-toggle" onClick={() => setExpanded(!expanded)}>
        <span className="tc-week-toggle-chevron">{expanded ? '▾' : '▸'}</span>
        <span className="tc-week-toggle-title">Week {week.roundNumber}</span>
        {week.wasStasisLocked && <span className="tc-stasis-badge">Frozen</span>}
        {!expanded && (
          <span className="tc-week-toggle-summary" style={{ color: deltaColor }}>
            {week.score > 0
              ? `${formatScoreAbbreviated(week.score)} vs ${formatScoreAbbreviated(week.targetWithRust)} (${isAhead ? '+' : ''}${formatScoreAbbreviated(deltaPoints)})`
              : 'No score yet'}
          </span>
        )}
        {expanded && (
          <StatusBadge variant={isCompleted ? 'info' : 'success'} uppercase size="sm">
            {isCompleted ? 'Completed' : 'In Progress'}
          </StatusBadge>
        )}
      </button>

      {expanded && (
        <>
          <div className="tc-stat-row tc-stat-row-quad" style={{ marginTop: 14 }}>
            <button type="button" className="tc-stat-tile tc-stat-tile-tappable" onClick={() => setShowTargetModal(true)}>
              <div className="tc-stat-value">{formatScoreAbbreviated(week.targetWithRust)}</div>
              <div className="tc-stat-label">Target</div>
            </button>
            <button type="button" className="tc-stat-tile tc-stat-tile-tappable" onClick={() => setShowScoreModal(true)}>
              <div className="tc-stat-value" style={{ color: deltaColor }}>
                {week.score > 0 ? formatScoreAbbreviated(week.score) : ' - '}
              </div>
              <div className="tc-stat-label">Your Best Score</div>
            </button>
            <button type="button" className="tc-stat-tile tc-stat-tile-tappable" onClick={() => setShowSurplusModal(true)}>
              <div className="tc-stat-value" style={{ color: deltaColor }}>
                {deltaPoints === null ? ' - ' : `${isAhead ? '▲ +' : '▼ '}${formatScoreAbbreviated(deltaPoints)}`}
              </div>
              <div className="tc-stat-label">{deltaPoints === null ? 'No Score Yet' : (isAhead ? 'Surplus (pts)' : 'Deficit (pts)')}</div>
            </button>
            <button type="button" className="tc-stat-tile tc-stat-tile-tappable" onClick={() => setShowBatteryImpactModal(true)}>
              <div className="tc-stat-value" style={{ color: batteryLossColor }}>
                {batteryLoss === null ? ' - ' : (batteryLoss > 0 ? `-${formatPercent(batteryLoss, 1)}` : '0%')}
              </div>
              <div className="tc-stat-label">
                {frozen ? 'Frozen' : (isCompleted ? 'Battery Lost' : 'Battery at Risk')}
              </div>
            </button>
          </div>

          {showTargetModal && <TargetCalculationModal week={week} onClose={() => setShowTargetModal(false)} />}
          {showScoreModal && <ScoreInfoModal onClose={() => setShowScoreModal(false)} />}
          {showSurplusModal && <SurplusInfoModal week={week} surplusPercent={surplusPercent} onClose={() => setShowSurplusModal(false)} />}
          {showBatteryImpactModal && (
            <BatteryImpactModal
              week={week}
              isCompleted={isCompleted}
              frozen={frozen}
              hasScore={hasScore}
              batteryLoss={batteryLoss}
              missPercent={missPercent}
              surplusPercent={surplusPercent}
              currentBattery={currentBattery}
              onClose={() => setShowBatteryImpactModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

function MyCompetition() {
  const [currentUser, setCurrentUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sternUsername, setSternUsername] = useState('');
  const [sternUsernameDraft, setSternUsernameDraft] = useState('');
  const [editingSternUsername, setEditingSternUsername] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [showBatteryModal, setShowBatteryModal] = useState(false);
  const [showRustModal, setShowRustModal] = useState(false);
  const [devices, setDevices] = useState([]);
  const [remoteCreditLoading, setRemoteCreditLoading] = useState({});
  const [confirmState, setConfirmState] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const CREDIT_AMOUNT = 1.0;
  const CREDIT_COST = 1.0;

  const fetchStatus = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError('');
      const [statusRes, historyRes] = await Promise.all([
        apiService.getUserCompetitionStatus(userId),
        apiService.getUserCompetitionHistory(userId)
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load competition status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    if (user) {
      fetchStatus(user.id);
      // GET /users/:id is admin-only - fetching your own profile has to go through the
      // self-scoped /users/me instead, or this silently 403s for every non-admin member (the
      // failure was swallowed here, so it looked like the Stern username was never set at all).
      apiService.getCurrentUser().then(res => setSternUsername(res.data?.stern_username || '')).catch(() => {});
    }

    apiService.getDeviceStatus().then(res => setDevices(res.data || [])).catch(() => {});

    const unsubscribe = websocketService.on('device_status', (data) => {
      const { deviceId, isOnline, localIp, remoteIp, isStale } = data;
      setDevices(prev => prev.map(device => (
        device.id === deviceId
          ? {
              ...device,
              isOnline,
              local_ip: localIp || device.local_ip,
              remote_ip: remoteIp || device.remote_ip,
              connection: {
                ...(device.connection || {}),
                isStale: !!isStale,
                localIp: localIp || (device.connection && device.connection.localIp) || null,
                remoteIp: remoteIp || (device.connection && device.connection.remoteIp) || null
              }
            }
          : device
      )));
    });

    return () => unsubscribe();
  }, [fetchStatus]);

  // The Transformers pinball cabinet is whichever active device has "transformers" in its
  // name - there's no dedicated competition->device FK, so this mirrors the machine_title_filter
  // matching used server-side for Stern leaderboard sync.
  const transformersDevices = devices.filter(d => d.is_active && /transformers/i.test(d.device_name || ''));

  const handleAddRemoteCredit = (deviceId, deviceName) => {
    setConfirmState({
      title: 'Add Credit?',
      message: `Add ${CREDIT_AMOUNT} credit to ${deviceName}? This will deduct $${CREDIT_COST.toFixed(2)} from your balance.`,
      confirmLabel: 'Add Credit',
      danger: false,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          setRemoteCreditLoading(prev => ({ ...prev, [deviceId]: true }));

          const response = await apiService.addRemoteCredit(deviceId);
          const result = response.data;

          setConfirmState(null);

          if (result?.status === 'confirmed') {
            showToast(`Credit added to ${deviceName}! Enjoy the game!`, 'success');
          } else {
            showToast(`Credit request sent to ${deviceName}, but confirmation status was ${result?.status || 'unknown'}.`, 'warning');
          }
        } catch (err) {
          const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to add credit';
          showToast(`Error: ${errorMsg}`, 'error');
          setConfirmState(null);
        } finally {
          setRemoteCreditLoading(prev => ({ ...prev, [deviceId]: false }));
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleUpdateSternUsername = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    try {
      setSavingUsername(true);
      const trimmed = sternUsernameDraft.trim();
      await apiService.updateUser(currentUser.id, { sternUsername: trimmed });
      const updatedUser = { ...currentUser, sternUsername: trimmed };
      authService.setCurrentUser(updatedUser);
      setCurrentUser(updatedUser);
      setSternUsername(trimmed);
      setEditingSternUsername(false);
      fetchStatus(currentUser.id);
    } catch (err) {
      showToast('Failed to update Stern username: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSavingUsername(false);
    }
  };

  if (loading) return <div className="loading">Loading your competition status...</div>;

  return (
    <div className="tc-page">
      <div className="tc-hero">
        <img src={transformersHero} alt="Transformers: More Than Meets The Eye pinball machine artwork" className="tc-hero-banner" />
        <h1>Transformers Pinball Championship</h1>
        <p>{status?.competition?.name || 'No active competition right now'}</p>
        {status?.currentWeek?.roundStatus === 'active' && (
          <div style={{ marginTop: 8, maxWidth: 260 }}>
            <RoundCountdown />
          </div>
        )}
      </div>

      {error && <div className="tc-danger-box">{error}</div>}

      {!sternUsername && (
        <div className="tc-section">
          <h2>Stern Insider Connected Account</h2>
          {!editingSternUsername ? (
            <div>
              <p>You haven't set your Stern username yet, so your scores can't be matched automatically.</p>
              <button
                type="button"
                className="tc-btn tc-btn-primary"
                onClick={() => {
                  setSternUsernameDraft(sternUsername);
                  setEditingSternUsername(true);
                }}
              >
                Set Username
              </button>
            </div>
          ) : (
            <form onSubmit={handleUpdateSternUsername}>
              <div className="tc-form-group">
                <label>Stern Insider Connected username</label>
                <input
                  type="text"
                  value={sternUsernameDraft}
                  onChange={(e) => setSternUsernameDraft(e.target.value)}
                  placeholder="Enter your Stern username"
                  required
                />
                <span className="tc-form-hint">Must match your username on Stern Pinball's leaderboard system.</span>
              </div>
              <button type="submit" className="tc-btn tc-btn-primary" disabled={savingUsername}>
                {savingUsername ? 'Saving...' : 'Save'}
              </button>{' '}
              <button type="button" className="tc-btn tc-btn-secondary" onClick={() => setEditingSternUsername(false)}>
                Cancel
              </button>
            </form>
          )}
        </div>
      )}

      {!status || !status.competition ? (
        <div className="tc-empty-state">
          <h2>No active competition</h2>
          <p>There's no Transformers Championship season running right now.</p>
        </div>
      ) : !status.participating ? (
        <div className="tc-empty-state">
          <h2>You're not enrolled in this season</h2>
          <p>
            {sternUsername
              ? "You'll be enrolled automatically and assigned to a team the next time scores sync - usually within the hour."
              : 'Set your Stern username above and you\'ll be enrolled automatically and assigned to a team the next time scores sync.'}
          </p>
        </div>
      ) : (
        <>
          <div className="tc-section">
            <div className="tc-section-header-row">
              <h2>Your Status</h2>
              <span className={`tc-team-badge team-${status.team}`}>
                <TeamIcon teamId={status.team} size={16} /> {getTeamMeta(status.team).name}
              </span>
            </div>

            {status.isStasisLocked && (
              <StatusBadge variant="neutral" uppercase>
                ⏸ Stasis Locked - Out of Contention
              </StatusBadge>
            )}

            <div className="tc-stat-row" style={{ marginTop: 12 }}>
              <button type="button" className="tc-stat-tile tc-stat-tile-tappable" onClick={() => setShowBatteryModal(true)}>
                <div className="tc-stat-value"><BatteryMeter battery={status.energonBattery} isStasisLocked={status.isStasisLocked} /></div>
                <div className="tc-stat-label">Energon Battery</div>
              </button>
              <button type="button" className="tc-stat-tile tc-stat-tile-tappable" onClick={() => setShowRustModal(true)}>
                <div className="tc-stat-value">{status.rustPoints}</div>
                <div className="tc-stat-label">Rust Points</div>
              </button>
            </div>

            {showBatteryModal && <BatteryInfoModal onClose={() => setShowBatteryModal(false)} />}
            {showRustModal && <RustInfoModal onClose={() => setShowRustModal(false)} />}

            {status.isStasisLocked && (
              <div className="tc-warning-box" style={{ marginTop: 14 }}>
                You're in Stasis Lock and out of contention for Grand Champion - but you can still play,
                and every point you score above your target helps your team win the weekly Energon Ranking.
              </div>
            )}

            {transformersDevices.length > 0 && (
              <div style={{ marginTop: 14 }}>
                {transformersDevices.map(device => (
                  <button
                    key={device.id}
                    type="button"
                    className="tc-btn tc-btn-primary tc-btn-block"
                    onClick={() => handleAddRemoteCredit(device.id, device.device_name)}
                    disabled={!device.isOnline || remoteCreditLoading[device.id]}
                  >
                    {remoteCreditLoading[device.id]
                      ? '⏳ Adding Credit...'
                      : device.isOnline
                        ? `⚡ Add Credit to ${device.device_name} ($${CREDIT_COST.toFixed(2)})`
                        : `❌ ${device.device_name} - Offline`}
                  </button>
                ))}
              </div>
            )}
          </div>

          {history.map((week, index) => (
            <WeekCard
              key={week.roundNumber}
              week={week}
              defaultExpanded={index === 0}
              currentBattery={status.energonBattery}
              currentlyStasisLocked={status.isStasisLocked}
            />
          ))}
        </>
      )}

      <CompetitionBottomNav />

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default MyCompetition;
// Exported for tests: the target breakdown has to keep adding up on both sides of the formula
// cutover, which is a rendering property and only checkable by rendering it.
export { TargetCalculationModal };
